import React, { useState, useEffect } from "react";
import { Box, Dialog, Button } from "@mui/material";
import { Link2 } from "lucide-react";
import { createRoot } from "react-dom/client";
import TextField from "components/common/TextField";

interface URLInputDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (url: string, text?: string) => void;
  initialUrl?: string;
  initialText?: string;
}

const URLInputDialog = ({
  open,
  onClose,
  onConfirm,
  initialUrl = "",
  initialText = "",
}: URLInputDialogProps) => {
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setUrl(initialUrl);
      setText(initialText);
      setError("");
    }
  }, [open, initialUrl, initialText]);

  const validateUrl = (inputUrl: string): boolean => {
    if (!inputUrl.trim()) {
      setError("URL is required");
      return false;
    }

    try {
      const urlToTest = inputUrl.startsWith("http")
        ? inputUrl
        : `https://${inputUrl}`;
      new URL(urlToTest);
      setError("");
      return true;
    } catch {
      setError("Please enter a valid URL");
      return false;
    }
  };

  const handleConfirm = () => {
    if (validateUrl(url)) {
      const finalUrl = url.startsWith("http") ? url : `https://${url}`;
      onConfirm(finalUrl, text.trim() || undefined);
      handleClose();
    }
  };

  const handleClose = () => {
    setUrl("");
    setText("");
    setError("");
    onClose();
  };

  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newUrl = event.target.value;
    setUrl(newUrl);

    if (error && newUrl.trim()) {
      setError("");
    }
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setText(event.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleConfirm();
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          p: 3,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "stretch",
          flexDirection: "column",
        }}
      >
        <TextField
          autoFocus
          fullWidth
          placeholder="Type or paste a link"
          value={url}
          onChange={handleUrlChange}
          error={!!error}
          helperText={error}
          InputProps={{
            startAdornment: (
              <Link2 style={{ color: "action.active", marginRight: "8px" }} />
            ),
          }}
          onKeyDown={handleKeyDown}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
            },
          }}
        />
        <TextField
          fullWidth
          placeholder="Link text (optional)"
          value={text}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
            },
          }}
        />
        <Button
          variant="text"
          onClick={handleConfirm}
          disabled={url.trim().length === 0 || !!error}
          disableRipple={true}
          sx={{
            textTransform: "none",
            color: "#198282",
            minWidth: "80px",
            alignSelf: "flex-end",
            "& .MuiTouchRipple-root": {
              color: "transparent !important",
            },
          }}
        >
          Apply
        </Button>
      </Box>
    </Dialog>
  );
};

let dialogContainer: HTMLDivElement | null = null;
let dialogRoot: any = null;

export const showURLDialog = (
  initialUrl: string,
  onConfirm: (url: string, text?: string) => void,
  initialText?: string,
) => {
  // Create container if it doesn't exist
  if (!dialogContainer) {
    dialogContainer = document.createElement("div");
    dialogContainer.id = "url-dialog-container";
    document.body.appendChild(dialogContainer);
    dialogRoot = createRoot(dialogContainer);
  }

  const handleClose = () => {
    if (dialogRoot && dialogContainer) {
      dialogRoot.render(null);
    }
  };

  const handleConfirm = (url: string, text?: string) => {
    onConfirm(url, text);
    handleClose();
  };

  dialogRoot.render(
    <URLInputDialog
      open={true}
      onClose={handleClose}
      onConfirm={handleConfirm}
      initialUrl={initialUrl}
      initialText={initialText}
    />,
  );
};

export default URLInputDialog;
